export enum PLAY_LIBRARY {
    /**
     * @value Always available
     * @description If this radio is selected, the video published will always be available
     */
    ALWAYS_AVAILABLE = 'PLAY.ALWAYS_AVAILABLE',
    /**
     * @value The video will be available until it is manually unpublished, archived or deleted.
     * @description The published video will always be available until certain status changes
     */
    AVAILABLE_UNTIL_STATUS_CHANGE = 'PLAY.AVAILABLE_UNTIL_STATUS_CHANGE',
    /**
     * @value Choose when to end your video publication or not.
     * @description Explain to the user how to use the date time picker to choose when a publication will end
     */
    CHOOSE_END_OF_PUBLICATION = 'PLAY.CHOOSE_END_OF_PUBLICATION',
    /**
     * @value Choose a date and time to unpublish the video. When the unpublish date is reached, the video will be set to the expired status.
     * @description Radio helper to explain what the time selection will do
     * When selecting a date, the video will expire after the selected delay
     */
    CHOOSE_UNPUBLISH_DATE = 'PLAY.CHOOSE_UNPUBLISH_DATE',
    /**
     * @value {"one":"Delete %NB% video","other":"Delete %NB% videos"}
     * @description Number de views on the video
     */
    DELETE_DIALOG_DELETE_CTA = 'PLAY.DELETE_DIALOG_DELETE_CTA',
    /**
     * @value Deleting the video %TITLE% is irreversible.
     * @description Message in the delete video modal to warn the user
     */
    DELETE_DIALOG_IRREVERSIBLE = 'PLAY.DELETE_DIALOG_IRREVERSIBLE',
    /**
     * @value {"one":"Keep it","other":"Keep them"}
     * @description Number de views on the video
     */
    DELETE_DIALOG_KEEP_CTA = 'PLAY.DELETE_DIALOG_KEEP_CTA',
    /**
     * @value {"one":"%NB% video will be deleted permanently. This action cannot be reverted.","other":"%NB% videos will be deleted permanently. This action cannot be reverted."}
     * @description Number of videos to delete
     */
    DELETE_DIALOG_MESSAGE = 'PLAY.DELETE_DIALOG_MESSAGE',
    /**
     * @value {"one":"Delete %NB% video","other":"Delete %NB% videos"}
     * @description Number de views on the video
     */
    DELETE_DIALOG_TITLE = 'PLAY.DELETE_DIALOG_TITLE',
    /**
     * @value The video will not be available anymore in the post or article where it was added.
     * @description You can't play a video that have been deleted, so we warn the user about this before he delete his video
     */
    DELETE_DIALOG_VIDEO_UNAVAILABLE = 'PLAY.DELETE_DIALOG_VIDEO_UNAVAILABLE',
    /**
     * @value Describe the video.
     * @description Subtitle of the upload video dialog in the "General" section
     */
    DESCRIBE_VIDEO = 'PLAY.DESCRIBE_VIDEO',
    /**
     * @value Download video
     * @description Text used as a label on a button allowing the user to download the video source.
     */
    DOWNLOAD_VIDEO = 'PLAY.DOWNLOAD_VIDEO',
    /**
     * @value Embed code
     */
    EMBED_CODE = 'PLAY.EMBED_CODE',
    /**
     * @value Embed code copied to the clipboard
     */
    EMBED_CODE_COPIED = 'PLAY.EMBED_CODE_COPIED',
    /**
     * @value Enter a short description of the video
     * @description Text that can be used to prompt our users to enter a short description of a video
     */
    ENTER_VIDEO_DESCRIPTION = 'PLAY.ENTER_VIDEO_DESCRIPTION',
    /**
     * @value Enter the title of the video
     * @description Text that can be used to prompt our users to enter the title of a video
     */
    ENTER_VIDEO_TITLE = 'PLAY.ENTER_VIDEO_TITLE',
    /**
     * @value Generate description
     * @description Tooltip telling the user that the button will generate a description
     */
    GENERATE_DESCRIPTION = 'PLAY.GENERATE_DESCRIPTION',
    /**
     * @value Generate tags
     * @description Tooltil on the generate button
     */
    GENERATE_TAGS = 'PLAY.GENERATE_TAGS',
    /**
     * @value Generate title
     * @description Tooltip telling the user the button will generate a title
     */
    GENERATE_TITLE = 'PLAY.GENERATE_TITLE',
    /**
     * @value Generating description will replace the existing description.
     * @description Body of the warning dialog showing when a description is already present in the field
     */
    GENERATING_DESCRIPTION_REPLACE_EXISTING_DESCRIPTION = 'PLAY.GENERATING_DESCRIPTION_REPLACE_EXISTING_DESCRIPTION',
    /**
     * @value Generating the description requires subtitles created from the audio.
     * @description Body of the warning dialog asking to create a transcript to generate a description
     */
    GENERATING_DESCRIPTION_REQUIRE_TRANSCRIPT = 'PLAY.GENERATING_DESCRIPTION_REQUIRE_TRANSCRIPT',
    /**
     * @value Generating tags will replace the existing tags.
     * @description Body of the warning dialog showing when a tag is already present in the field
     */
    GENERATING_TAGS_REPLACE_EXISTING_TAGS = 'PLAY.GENERATING_TAGS_REPLACE_EXISTING_TAGS',
    /**
     * @value Generating tags requires subtitles created from the audio.
     * @description Body of the warning dialog asking to create a transcript to generate a tag
     */
    GENERATING_TAGS_REQUIRE_TRANSCRIPT = 'PLAY.GENERATING_TAGS_REQUIRE_TRANSCRIPT',
    /**
     * @value Generating title will replace the existing title.
     * @description Body of the warning dialog showing when a title is already present in the field
     */
    GENERATING_TITLE_REPLACE_EXISTING_TITLE = 'PLAY.GENERATING_TITLE_REPLACE_EXISTING_TITLE',
    /**
     * @value Generating the title requires subtitles created from the audio.
     * @description Body of the warning dialog asking to create a transcript to generate a title
     */
    GENERATING_TITLE_REQUIRE_TRANSCRIPT = 'PLAY.GENERATING_TITLE_REQUIRE_TRANSCRIPT',
    /**
     * @value You must set your scheduled time in the future
     * @description Error occurring when you try to set a date in the past while scheduling a video
     */
    INCORRECT_SCHEDULE_DATE_IN_THE_PAST = 'PLAY.INCORRECT_SCHEDULE_DATE_IN_THE_PAST',
    /**
     * @value Interaction tools
     * @description Label of the tab under which the user can find all the tools related videos interactions (subtitles, chapters etc...)
     */
    INTERACTION_TOOLS = 'PLAY.INTERACTION_TOOLS',
    /**
     * @value Your file exceeds the maximum allowed size. The file must be %SIZE% or smaller. Reduce file size and try again.
     * @description Error for a premium play user that try to upload a big video
     */
    LIMITED_UPLOAD_SIZE = 'PLAY.LIMITED_UPLOAD_SIZE',
    /**
     * @value Your file exceeds the maximum allowed size. The file must be %SIZE% or smaller. Reduce file size or upgrade to the full version of Play for larger uploads.
     * @description Error message when you are not premium for Play and you try to upload a video too big
     */
    LIMITED_UPLOAD_SIZE_UPGRADE = 'PLAY.LIMITED_UPLOAD_SIZE_UPGRADE',
    /**
     * @value Most viewed
     * @description Used, for example, in a dropdown to sort videos by their number of views
     */
    MOST_VIEWED = 'PLAY.MOST_VIEWED',
    /**
     * @value Pick a specific date and time to publish your video.
     * @description Description of the date time picker for scheduling the publication of a video
     */
    PICK_SCHEDULED_DATE = 'PLAY.PICK_SCHEDULED_DATE',
    /**
     * @value Publish immediately
     * @description Toggle to allow videos to be published immediately
     */
    PUBLISH_NOW = 'PLAY.PUBLISH_NOW',
    /**
     * @value Choose to publish your video now or schedule the publication to a future date.
     * @description Description of the schedule section to explain the user they can choose between a scheduled event or publish now
     */
    PUBLISH_NOW_OR_SCHEDULE = 'PLAY.PUBLISH_NOW_OR_SCHEDULE',
    /**
     * @value Replace existing description?
     * @description Title of the warning dialog for replacing description
     */
    REPLACE_EXISTING_DESCRIPTION_TITLE = 'PLAY.REPLACE_EXISTING_DESCRIPTION_TITLE',
    /**
     * @value Replace existing tags?
     * @description Title of the warning dialog for replacing tags
     */
    REPLACE_EXISTING_TAGS_TITLE = 'PLAY.REPLACE_EXISTING_TAGS_TITLE',
    /**
     * @value Replace existing title?
     * @description Title of the warning dialog for replacing title
     */
    REPLACE_EXISTING_TITLE_TITLE = 'PLAY.REPLACE_EXISTING_TITLE_TITLE',
    /**
     * @value Save as draft
     * @description Notify the user that the video will be saved as draft (non published content)
     */
    SAVE_AS_DRAFT = 'PLAY.SAVE_AS_DRAFT',
    /**
     * @value End of publication
     * @description Title of the section to end the publication when the time set in the date time picker is reached
     */
    SCHEDULE_END_OF_PUBLICATION = 'PLAY.SCHEDULE_END_OF_PUBLICATION',
    /**
     * @value Schedule publication
     * @description Title of a section inside the upload/encoding video dialog
     */
    SCHEDULE_PUBLICATION = 'PLAY.SCHEDULE_PUBLICATION',
    /**
     * @value Select video
     * @description Text used for example to label a checkbox allowing the user to select a video
     */
    SELECT_VIDEO = 'PLAY.SELECT_VIDEO',
    /**
     * @value Starred
     */
    STARRED = 'FRONT.WIDGET_FILE_LIST.SETTINGS.FOLDER.DRIVE.STARRED',
    /**
     * @value Video tabs list
     * @description Display accessibility label for tab in video manager
     */
    TABS_LIST_ACCESSIBILITY_LABEL = 'PLAY.VIDEO_TABS_LIST_ACCESSIBILITY_LABEL',
    /**
     * @value Capture as thumbnail
     * @description Text used to label a button that allows a user to take a snapshot of a video
     */
    TAKE_SNAPSHOT = 'PLAY.TAKE_SNAPSHOT',
    /**
     * @value Thumbnail
     * @description Thumbnail
     */
    THUMBNAIL = 'PLAY.THUMBNAIL',
    /**
     * @value Auto generated subtitles
     * @description Transcription
     */
    TRANSCRIPT = 'PLAY.TRANSCRIPT',
    /**
     * @value Auto generated subtitles will generate a textual version of the video
     * @description Text used to explain to the user what a video transcript is.
     */
    TRANSCRIPT_DESCRIPTION = 'PLAY.TRANSCRIPT_DESCRIPTION',
    /**
     * @value Generate subtitles
     * @description Text used on a toggle switch where the user can choose whether to generate a video transcript or not.
     */
    TRANSCRIPT_GENERATE = 'PLAY.TRANSCRIPT_GENERATE',
    /**
     * @value Select the language spoken in the video to generate automatic subtitles. Generation will start when encoding is finished.
     * @description Warn the user about automatic translation, this information goes pair with a language selection
     */
    TRANSCRIPT_INFORMATION = 'PLAY.TRANSCRIPT_INFORMATION',
    /**
     * @value Spoken language of the video
     * @description Text used to label a select field where the user can choose the spoken language of the video to be transcribed.
     */
    TRANSCRIPT_LANGUAGE_LABEL = 'PLAY.TRANSCRIPT_LANGUAGE_LABEL',
    /**
     * @value An auto-generated subtitle file will be created.
     * @description Text that tells the user that an auto-generated subtitle track will be created for their video.
     */
    TRANSCRIPT_RESULT = 'PLAY.TRANSCRIPT_RESULT',
    /**
     * @value Generation will start once encoding is over.
     * @description Text used to explains the user when the video transcription job will start.
     */
    TRANSCRIPT_START_DESCRIPTION = 'PLAY.TRANSCRIPT_START_DESCRIPTION',
    /**
     * @value Upload video
     * @description Call to Action to select a video to import
     */
    UPLOAD_DIALOG_CTA = 'PLAY.UPLOAD_DIALOG_CTA',
    /**
     * @value Your drafts stay private, and visible by admins only.
     * @description Description of the dialog box to upload a video
     */
    UPLOAD_DIALOG_DESCRIPTION = 'PLAY.UPLOAD_DIALOG_DESCRIPTION',
    /**
     * @value Drag and drop the video you want to upload.
     * @description Subtitle of the dialog box to upload a video
     */
    UPLOAD_DIALOG_SUBTITLE = 'PLAY.UPLOAD_DIALOG_SUBTITLE',
    /**
     * @value Upload a video
     * @description Title of the dialog box to upload a video
     */
    UPLOAD_DIALOG_TITLE = 'PLAY.UPLOAD_DIALOG_TITLE',
    /**
     * @value From computer
     * @description List item from upload video dropdown
     */
    UPLOAD_FROM_COMPUTER = 'PLAY.UPLOAD_FROM_COMPUTER',
    /**
     * @value From Drive
     * @description List item from upload video dropdown
     */
    UPLOAD_FROM_DRIVE = 'PLAY.UPLOAD_FROM_DRIVE',
    /**
     * @value Upload new thumbnail
     * @description Call To Action to upload a thumbnail
     */
    UPLOAD_THUMBNAIL = 'PLAY.UPLOAD_THUMBNAIL',
    /**
     * @value Videos cannot be played while uploading and encoding.
     * @description Hint message explaining to the user that videos cannot be played while processing
     */
    VIDEOS_UNPLAYABLE_WHILE_PROCESSING = 'PLAY.VIDEOS_UNPLAYABLE_WHILE_PROCESSING',
    /**
     * @value Analytics
     * @description analytics
     */
    VIDEO_DIALOG_ANALYTICS_TAB_TITLE = 'PLAY.ANALYTICS',
    /**
     * @value Chapters
     * @description Chapters
     */
    VIDEO_DIALOG_CHAPTERS_TAB_TITLE = 'PLAY.CHAPTERS',
    /**
     * @value Details
     * @description Used for example in the edit dialog box to label the details panel.
     */
    VIDEO_DIALOG_DETAILS_TAB_TITLE = 'PLAY.DETAILS',
    /**
     * @value Select a file to replace thumbnail
     * @description ALT for the Call to Action to upload a new file to replace thumbnail
     */
    VIDEO_DIALOG_EDIT_THUMBNAIL_ALT = 'PLAY.VIDEO_DIALOG_EDIT_THUMBNAIL_ALT',
    /**
     * @value Placeholder
     */
    VIDEO_DIALOG_EDIT_THUMBNAIL_ALT_PLACEHOLDER = 'PLAY.VIDEO_DIALOG_EDIT_THUMBNAIL_ALT_PLACEHOLDER',
    /**
     * @value Subtitles
     * @description Used for example in the edit dialog box to label the subtitles panel.
     */
    VIDEO_DIALOG_SUBTITLES_TAB_TITLE = 'PLAY.SUBTITLES',
    /**
     * @value New video
     */
    VIDEO_DIALOG_TITLE_NEW_VIDEO = 'PLAY.VIDEO_DIALOG_TITLE_NEW_VIDEO',
    /**
     * @value Encoding...
     * @description Video encoding status
     */
    VIDEO_ENCODING = 'PLAY.VIDEO_ENCODING',
    /**
     * @value {"one":"Delete video","other":"Delete %NB% videos"}
     * @description Title and confirmation dialog for deleting a video.
     * For the singular, translate as in "Delete THE video" and not "delete A video", but omit "THE" if possible
     */
    VIDEO_LIBRARY_DELETE_SELECTED_CTA = 'PLAY.VIDEO_LIBRARY_DELETE_SELECTED_CTA',
    /**
     * @value Upload a video
     * @description Call To Action to upload a video in the video library
     */
    VIDEO_LIBRARY_UPLOAD_CTA = 'PLAY.VIDEO_LIBRARY_UPLOAD_CTA',
    /**
     * @value Link to the video
     * @description Label for the link of the video
     */
    VIDEO_LINK = 'PLAY.VIDEO_LINK',
    /**
     * @value You don't have any videos
     */
    VIDEO_LIST_EMPTY = 'PLAY.VIDEO_LIST_EMPTY',
    /**
     * @value Simply click on « Upload video » to create your first one.
     */
    VIDEO_LIST_EMPTY_TIPS = 'PLAY.VIDEO_LIST_EMPTY_TIPS',
    /**
     * @value Delete video
     * @description Delete video Tooltip
     */
    VIDEO_LIST_ITEM_DELETE_LABEL = 'PLAY.VIDEO_LIST_ITEM_DELETE_LABEL',
    /**
     * @value Edit video
     * @description Edit video
     */
    VIDEO_LIST_ITEM_EDIT_LABEL = 'PLAY.VIDEO_LIST_ITEM_EDIT_LABEL',
    /**
     * @value Uploaded on
     * @description Display the date of the upload
     */
    VIDEO_LIST_ITEM_UPDATED_AT = 'PLAY.VIDEO_LIST_ITEM_UPDATED_AT',
    /**
     * @value Title is required. To save changes, complete required fiels for all applicable languages. You can also clear optional fields for other languages.
     */
    VIDEO_TITLE_ERROR = 'PLAY.VIDEO_TITLE_ERROR_DESCRIPTION_WITHOUT_TITLE',
    /**
     * @value Replace video
     * @description Upload new video file to update video
     */
    VIDEO_UPLOAD_NEW_VERSION_CTA = 'PLAY.VIDEO_UPLOAD_NEW_VERSION_CTA',
    /**
     * @value You can save and close the window
     */
    VIDEO_UPLOAD_PROGRESS_ENCODING = 'PLAY.VIDEO_UPLOAD_PROGRESS_ENCODING',
    /**
     * @value After saving, your video will be available for everybody when encoding is over.
     */
    VIDEO_UPLOAD_PROGRESS_ENCODING_MESSAGE = 'PLAY.VIDEO_UPLOAD_PROGRESS_ENCODING_MESSAGE',
    /**
     * @value Video uploaded
     * @description Progress state of video : uploaded
     */
    VIDEO_UPLOAD_PROGRESS_UPLOADED = 'PLAY.VIDEO_UPLOAD_PROGRESS_UPLOADED',
    /**
     * @value Uploading...
     * @description Progress state of video uploading
     */
    VIDEO_UPLOAD_PROGRESS_UPLOADING = 'PLAY.VIDEO_UPLOAD_PROGRESS_UPLOADING',
    /**
     * @value Do not close this window while the video is uploading.
     * @description Warning message when video is uplaoding
     */
    VIDEO_UPLOAD_PROGRESS_WARNING = 'PLAY.VIDEO_UPLOAD_PROGRESS_WARNING',
    /**
     * @value Current version of the video is still available as long as this version is not uploaded, encoded and published.
     */
    VIDEO_UPLOAD_PROGRESS_WARNING_MESSAGE = 'PLAY.VIDEO_UPLOAD_PROGRESS_WARNING_MESSAGE',
    /**
     * @value All users can watch the video.
     * @description Description of the status for the public videos
     */
    VISIBILITY_PUBLIC_DESCRIPTION = 'PLAY.VISIBILITY_PUBLIC_DESCRIPTION',
}
